import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QuickBuy from '@/components/QuickBuy';
import { ArrowRight, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { swellProducts } from '@/lib/swell';
import { useSiteContent } from '@/hooks/useSiteContent';

const MEDIA = {
  hero: "/images/hero-banner.jpg",
  lookbook_hero: "/images/lookbook-studio.jpg",
  collection_edgy: "/images/collection-edge.jpg",
  collection_street: "/images/collection-street.jpg",
};

function ProductCard({ product, index }) {
  const imageUrl = product.image_url;
  const hoverImageUrl = product.images?.[1] || null;
  const [quickBuyOpen, setQuickBuyOpen] = useState(false);

  return (
    <div
      className="group cursor-pointer opacity-0 animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`product-card-${product.id || index}`}
    >
      <Link to={`/product/${product.slug || product.id}`}>
        <div className="relative overflow-hidden aspect-[4/5] bg-gray-100">
          {imageUrl && (
            <>
              <img
                src={imageUrl}
                alt={product.name}
                className={`w-full h-full object-cover transition-opacity duration-500 ${hoverImageUrl ? 'group-hover:opacity-0' : 'group-hover:scale-105'}`}
                loading="lazy"
                decoding="async"
              />
              {hoverImageUrl && (
                <img
                  src={hoverImageUrl}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  loading="lazy"
                  decoding="async"
                />
              )}
            </>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
        </div>
      </Link>
      <div className="mt-3 flex justify-between items-start">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider">{product.name}</h3>
          <p className="text-sm mt-1 text-gray-600">
            {product.sale_price ? (
              <>
                ${Number(product.sale_price).toFixed(2)}
                <span className="ml-2 line-through text-gray-400">${Number(product.price || 0).toFixed(2)}</span>
              </>
            ) : (
              <>${Number(product.price || 0).toFixed(2)}</>
            )}
          </p>
        </div>
        <button
          onClick={(e) => { e.preventDefault(); setQuickBuyOpen(true); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-2 border border-black hover:bg-black hover:text-white"
          data-testid={`add-to-cart-${product.id || index}`}
        >
          <ShoppingBag className="w-4 h-4" />
        </button>
        {quickBuyOpen && <QuickBuy product={product} onClose={() => setQuickBuyOpen(false)} />}
      </div>
    </div>
  );
}

const DEFAULTS = {
  hero_subtitle: "Women's Streetwear",
  hero_title: "BREAK THE RULES",
  hero_description: "Bold designs for those who dare to stand out.",
  hero_cta: "SHOP NOW",
  featured_eyebrow: "What's New",
  featured_title: "Featured",
  img_hero: "https://tspm-api.abrandfantastic.workers.dev/api/r2/site-img_hero",
  img_hero_2: "",
  img_hero_3: "",
  img_collection_1: "/images/collection-edge.jpg",
  img_collection_2: "/images/collection-street.jpg",
  img_lookbook: "/images/lookbook-studio.jpg",
  editorial_1_eyebrow: "Collection 01",
  editorial_1_title: "Edge",
  editorial_1_cta: "Explore",
  editorial_2_eyebrow: "Collection 02",
  editorial_2_title: "Street",
  editorial_2_cta: "View",
  editorial_3_eyebrow: "Lookbook",
  editorial_3_title: "Studio",
  editorial_3_cta: "View",
  brand_eyebrow: "Our Philosophy",
  brand_title_1: "FASHION WITHOUT APOLOGY.",
  brand_title_2: "RULES WITHOUT LIMITS.",
  brand_cta: "Our Story",
};

function HeroSection({ content }) {
  const slides = [content.img_hero, content.img_hero_2, content.img_hero_3]
    .filter(Boolean)
    .map(url => url.split('?')[0]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    const timer = setInterval(() => {
      setActive(i => (i + 1) % slides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [slides.length, paused]);

  const prev = () => setActive(i => (i - 1 + slides.length) % slides.length);
  const next = () => setActive(i => (i + 1) % slides.length);

  return (
    <section
      id="hero"
      className="relative min-h-[85vh] flex items-center justify-center border-b border-black overflow-hidden"
      data-testid="hero-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Crossfade slides */}
      {slides.map((src, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === active ? 1 : 0 }}
        >
          <img
            src={src}
            alt="RAD Streetwear"
            className="w-full h-full object-cover"
            fetchPriority={i === 0 ? "high" : "auto"}
            loading={i === 0 ? "eager" : "lazy"}
            decoding={i === 0 ? "sync" : "async"}
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 text-center text-white px-6">
        <p className="text-xs tracking-[0.3em] uppercase font-manrope mb-4 opacity-80">{content.hero_subtitle}</p>
        <h1 className="font-bebas text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-none mb-6">
          {content.hero_title}
        </h1>
        <p className="text-base sm:text-lg font-manrope tracking-wide max-w-md mx-auto mb-8 opacity-90">
          {content.hero_description}
        </p>
        <Link to="/shop">
          <Button
            className="rounded-none bg-white text-black hover:bg-black hover:text-white border-2 border-white hover:border-white px-8 py-6 text-xs tracking-[0.2em] uppercase font-bold transition-all duration-300"
            data-testid="hero-shop-now-btn"
          >
            {content.hero_cta}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* Controls — only shown when multiple slides exist */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center border border-white/60 text-white hover:bg-white/20 transition-colors"
            aria-label="Previous slide"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
          </button>
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center border border-white/60 text-white hover:bg-white/20 transition-colors"
            aria-label="Next slide"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className="w-12 h-12 flex items-center justify-center"
                aria-label={`Go to slide ${i + 1}`}
              >
                <span className={`block w-2 h-2 rounded-full transition-all duration-300 ${i === active ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'}`} />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function FeaturedProducts({ products, content }) {
  const displayProducts = products.length > 0
    ? products.slice(0, 4)
    : [
        { id: 'f1', name: 'Oversized Cargo Jacket', price: 189, slug: 'f1', image_url: MEDIA.product_1 },
        { id: 'f2', name: 'Wide Leg Trousers', price: 129, slug: 'f2', image_url: MEDIA.product_2 },
        { id: 'f3', name: 'Leather Moto Vest', price: 245, slug: 'f3', image_url: MEDIA.product_3 },
        { id: 'f4', name: 'Color Block Windbreaker', price: 165, slug: 'f4', image_url: MEDIA.product_4 },
      ];

  return (
    <section id="featured" className="py-16 lg:py-24 px-6 lg:px-10 max-w-[1600px] mx-auto" data-testid="featured-products">
      <div className="flex justify-between items-end mb-12">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-2">{content.featured_eyebrow}</p>
          <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight uppercase">{content.featured_title}</h2>
        </div>
        <Link
          to="/shop"
          className="text-xs tracking-[0.15em] uppercase font-bold hover:opacity-60 transition-opacity flex items-center gap-2"
          data-testid="view-all-link"
        >
          View All <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {displayProducts.map((product, i) => (
          <ProductCard key={product.id} product={product} index={i} />
        ))}
      </div>
    </section>
  );
}

function EditorialGrid({ content }) {
  return (
    <section id="editorial" className="border-t border-black" data-testid="editorial-grid">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
        {/* Large image */}
        <div className="md:col-span-8 relative overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[600px] border-b md:border-b-0 md:border-r border-black">
          <img
            src={content.img_collection_1}
            alt="Edgy Collection"
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-black/30 flex flex-col justify-end p-8 lg:p-12">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-2">{content.editorial_1_eyebrow}</p>
            <h2 className="font-bebas text-4xl sm:text-5xl text-white tracking-tight uppercase mb-4">{content.editorial_1_title}</h2>
            <Link to="/collections">
              <Button
                className="rounded-none bg-white text-black hover:bg-black hover:text-white border-2 border-white hover:border-white w-fit px-6 py-5 text-xs tracking-[0.15em] uppercase font-bold transition-all"
                data-testid="collection-edge-btn"
              >
                {content.editorial_1_cta}
              </Button>
            </Link>
          </div>
        </div>

        {/* Stacked right */}
        <div className="md:col-span-4 flex flex-col">
          <div className="relative overflow-hidden flex-1 min-h-[300px] border-b border-black">
            <img
              src={content.img_collection_2}
              alt="Street Collection"
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-black/30 flex flex-col justify-end p-6">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-2">{content.editorial_2_eyebrow}</p>
              <h3 className="font-bebas text-3xl text-white tracking-tight uppercase mb-3">{content.editorial_2_title}</h3>
              <Link to="/collections">
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-white hover:opacity-60 transition-opacity flex items-center gap-2">
                  {content.editorial_2_cta} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            </div>
          </div>
          <div className="relative overflow-hidden flex-1 min-h-[300px]">
            <img
              src={content.img_lookbook}
              alt="Lookbook"
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-black/30 flex flex-col justify-end p-6">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/70 mb-2">{content.editorial_3_eyebrow}</p>
              <h3 className="font-bebas text-3xl text-white tracking-tight uppercase mb-3">{content.editorial_3_title}</h3>
              <Link to="/collections">
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-white hover:opacity-60 transition-opacity flex items-center gap-2">
                  {content.editorial_3_cta} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandStatement({ content }) {
  return (
    <section id="brand" className="py-20 lg:py-32 px-6 lg:px-10 max-w-[1600px] mx-auto text-center" data-testid="brand-statement">
      <p className="text-xs tracking-[0.3em] uppercase font-bold text-gray-400 mb-6">{content.brand_eyebrow}</p>
      <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight max-w-4xl mx-auto">
        {content.brand_title_1}
        <br />
        <span className="text-gray-400">{content.brand_title_2}</span>
      </h2>
      <Link to="/about">
        <Button
          className="mt-10 rounded-none bg-black text-white hover:bg-white hover:text-black border-2 border-black px-8 py-6 text-xs tracking-[0.2em] uppercase font-bold transition-all"
          data-testid="about-cta-btn"
        >
          {content.brand_cta} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const content = useSiteContent(DEFAULTS);

  useEffect(() => {
    let handle;
    const fetchProducts = async () => {
      try {
        const data = await swellProducts.list({ limit: 4 });
        setProducts(data.products || []);
      } catch (err) {
        console.error('Failed to fetch products:', err);
      }
    };

    if ('requestIdleCallback' in window) {
      handle = window.requestIdleCallback(fetchProducts);
    } else {
      handle = setTimeout(fetchProducts, 1000);
    }

    return () => {
      if ('cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      } else if (typeof handle === 'number') {
        clearTimeout(handle);
      }
    };
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection content={content} />
      <FeaturedProducts products={products} content={content} />
      <EditorialGrid content={content} />
      <BrandStatement content={content} />
    </div>
  );
}
